import React from 'react';
import { Link } from 'react-router-dom';
import {
  Layers,
  Sparkles,
  Wrench,
  Ruler,
  DoorOpen,
  Sofa,
  Shield,
  Grid3X3,
  Check,
  ArrowRight,
  Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import ContactForm from '../components/shared/ContactForm';
import { services, companyInfo } from '../data/mock';

const iconMap = {
  Layers,
  Sparkles,
  Wrench,
  Ruler,
  DoorOpen,
  Sofa,
  Shield,
  Grid3X3
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
              I Nostri Servizi
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6 leading-tight">
              Soluzioni professionali per il tuo legno
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Offriamo una gamma completa di servizi per la posa, manutenzione e 
              valorizzazione di pavimenti in legno e complementi d'arredo.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Layers;
              const isEven = index % 2 === 0;

              return (
                <div
                  key={service.id}
                  id={service.id}
                  className="scroll-mt-32"
                >
                  <div className={`grid lg:grid-cols-2 gap-12 items-center ${
                    !isEven ? 'lg:flex-row-reverse' : ''
                  }`}>
                    <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                      <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-[400px] object-cover"
                        />
                        <div className="absolute top-6 left-6">
                          <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center">
                            <IconComponent className="w-8 h-8 text-amber-600" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                      <span className="inline-block bg-amber-100 text-amber-700 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
                        Servizio #{String(index + 1).padStart(2, '0')}
                      </span>
                      <h2 className="text-3xl font-bold text-slate-800 mb-4">
                        {service.title}
                      </h2>
                      <p className="text-slate-600 text-lg leading-relaxed mb-6">
                        {service.fullDesc}
                      </p>

                      <div className="bg-slate-50 rounded-xl p-6 mb-6">
                        <h4 className="font-semibold text-slate-800 mb-4">I vantaggi:</h4>
                        <ul className="grid sm:grid-cols-2 gap-3">
                          {service.benefits.map((benefit, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-amber-600" />
                              </div>
                              <span className="text-slate-600 text-sm">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Link to="/preventivi">
                        <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                          Richiedi Preventivo
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Hai bisogno di un servizio personalizzato?
              </h2>
              <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                Contattaci per discutere le tue esigenze specifiche. Siamo pronti 
                a trovare la soluzione perfetta per te.
              </p>
              <div className="flex flex-wrap gap-6">
                <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-white">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Chiamaci</p>
                    <p className="font-semibold">{companyInfo.phone}</p>
                  </div>
                </a>
              </div>
            </div>

            <Card className="bg-white border-0 shadow-2xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-800 mb-6">Richiedi Informazioni</h3>
                <ContactForm />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
